import { FC, useState, SyntheticEvent } from 'react';
import { Chip } from '@mui/material';
import { styled } from '@mui/material/styles';
import Pagination from '@mui/material/Pagination';
import { useSelector } from 'react-redux';
import Moment from 'react-moment';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import { AxiosError } from 'axios';
import { useMutation } from 'react-query';
import MuiAccordionSummary, {
	AccordionSummaryProps,
} from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import { CwTrainingEmail } from '../../../lib/interfaces/CwTrainingEmail';
import ContentRenderer from '../../Smart/Content/ContentRenderer';
import { markAsReadCwTrainingEmail } from '../../../api/apiRequests/cwTrainingEmails';
import {
	IPagination,
	TPaginationSettings,
} from '../../../lib/interfaces/Pagination';
import { RootState } from '@/redux/store';

type CwTrainingEmailsListProps = {
	data: IPagination<CwTrainingEmail>;
	refetchEmails: () => void;
	setPagination: (paging: TPaginationSettings) => void;
};

const Accordion = styled((props: AccordionProps) => (
	<MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
	width: '100%',
	border: `1px solid ${theme.palette.divider}`,
	borderRadius: '10px',
	'&:not(:last-child)': {
		borderBottom: 0,
	},
	'&::before': {
		display: 'none',
	},
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => {
	const { isDarkTheme } = useSelector((state: RootState) => state.ui.theme);
	return (
		<MuiAccordionSummary
			expandIcon={
				<ArrowForwardIosSharpIcon
					sx={{ fontSize: '0.9rem', color: isDarkTheme ? '#fff' : '#222' }}
				/>
			}
			{...props}
		/>
	);
})(({ theme }) => ({
	width: '100%',
	backgroundColor: 'rgba(110, 110, 110, 0.05)',
	borderRadius: '10px',
	flexDirection: 'row-reverse',
	transition: 'border-radius 0.5s linear',
	'&.Mui-expanded': {
		backgroundColor: 'rgba(110, 110, 110, 0.1)',
		borderRadius: '10px 10px 0 0',
	},
	'& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
		transform: 'rotate(90deg)',
	},
	'& .MuiAccordionSummary-content': {
		marginLeft: theme.spacing(1),
	},
	...theme.applyStyles('dark', {
		backgroundColor: 'rgba(255, 255, 255, .05)',
	}),
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
	padding: theme.spacing(2),
}));

const CwTrainingEmailsList: FC<CwTrainingEmailsListProps> = ({
	data,
	refetchEmails,
	setPagination,
}) => {
	const [expanded, setExpanded] = useState<string | false>(false);

	const markAsReadMutation = useMutation<
		CwTrainingEmail | undefined,
		AxiosError,
		number
	>((emailId: number) => markAsReadCwTrainingEmail(emailId), {
		// On success, update the cache
		onSuccess: (response: CwTrainingEmail | undefined) => {
			console.log('response', response);
			if (response?.read) {
				refetchEmails();
			}
		},
	});

	const handleChange =
		(item: CwTrainingEmail) => (event: SyntheticEvent, newExpanded: boolean) => {
			setExpanded(newExpanded ? item.id.toString() : false);
			if (!item.read) {
				markAsReadMutation.mutate(item.id);
			}
		};

	const handlePageChange = (page: number) => {
		setPagination({ page, limit: data.meta.itemsPerPage });
	};

	return (
		<div style={{ width: '100%' }}>
			{data?.items.map((item) => (
				<Accordion
					expanded={expanded === item.id.toString()}
					onChange={handleChange(item)}
					sx={{ width: '100%' }}
				>
					<AccordionSummary
						aria-controls="panel1d-content"
						id="panel1d-header"
						sx={{ width: '100%' }}
					>
						<div
							style={{
								display: 'flex',
								justifyContent: 'space-between',
								width: '100%',
							}}
						>
							<div style={{ display: 'flex' }}>
								{!item.read && (
									<Chip sx={{ marginRight: '10px' }} label="New" size="small" />
								)}
								<Typography sx={{ fontWeight: item.read ? 'normal' : '500' }}>
									{item.content.subject}
								</Typography>
							</div>
							<div>
								<Typography
									variant="caption"
									sx={{ fontWeight: item.read ? 'normal' : '500' }}
								>
									<Moment format="DD MMM YYYY, hh:mm A">{item.created}</Moment>
								</Typography>
							</div>
						</div>
					</AccordionSummary>
					<AccordionDetails
						sx={{
							width: '100%',
							display: 'flex',
							flexDirection: 'column',
							gap: '20px',
							padding: '30px 20px',
						}}
					>
						<div
							style={{
								display: 'flex',
								width: '100%',
								gap: '20px',
							}}
						>
							<div
								style={{
									background: 'rgba(110, 110, 110, 0.125)',
									padding: '5px 10px',
									borderRadius: '10px',
									display: 'flex',
									gap: '10px',
								}}
							>
								<Typography sx={{ fontWeight: '500' }}>To:</Typography>{' '}
								<Typography>{item.content.to}</Typography>
							</div>
							<div
								style={{
									background: 'rgba(110, 110, 110, 0.125)',
									padding: '5px 10px',
									borderRadius: '10px',
									display: 'flex',
									gap: '10px',
								}}
							>
								<Typography sx={{ fontWeight: '500' }}>From:</Typography>{' '}
								<Typography>{item.content.from}</Typography>
							</div>
						</div>
						<div>
							<Typography>
								<ContentRenderer html={item.content.content} />
							</Typography>
						</div>
					</AccordionDetails>
				</Accordion>
			))}
			{data.meta.totalPages > 1 && (
				<Pagination
					sx={{ marginTop: '10px' }}
					count={data.meta.totalPages}
					page={data.meta.currentPage}
					onChange={(_e, page: number) => handlePageChange(page)}
				/>
			)}
		</div>
	);
};

export default CwTrainingEmailsList;
